import z from 'zod';

export const ImageFieldSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unchanged'), url: z.string() }),
  z.object({ kind: z.literal('new'), previewUrl: z.string(), file: z.file() }),
  z.object({ kind: z.literal('removed') }),
  z.object({ kind: z.literal('empty') }),
]);

const ValidImageFieldSchema = ImageFieldSchema;

export type ImageField = z.infer<typeof ImageFieldSchema>;

type ValidImageField = z.infer<typeof ValidImageFieldSchema>;

export type ImageWriteRef =
  | { kind: 'url'; url: string }
  | { kind: 'file'; file: File }
  | { kind: 'none' };

const getImageWriteRef = (input: ImageField): ImageWriteRef => {
  switch (input.kind) {
    case 'unchanged':
      return { kind: 'url', url: input.url };
    case 'new':
      return { kind: 'file', file: input.file };
    case 'removed':
    case 'empty':
      return { kind: 'none' };
  }
};

const PLACEHOLDER_IMAGE_URL =
  'https://placehold.co/600x400?text=set\nyour\nimage';

const getImagePreviewUrl = (input: ImageField) => {
  switch (input.kind) {
    case 'empty':
      return PLACEHOLDER_IMAGE_URL;

    case 'removed':
      return PLACEHOLDER_IMAGE_URL;

    case 'new':
      return input.previewUrl;

    case 'unchanged':
      return input.url;
  }
};

const initImageUploadField = (imageUrl: string | undefined | null) => {
  if (imageUrl === null) {
    return {
      kind: 'empty',
    } as const satisfies ImageField;

    return {
      kind: 'unchanged',
      url: PLACEHOLDER_IMAGE_URL,
    } as const satisfies ValidImageField;
  }

  if (!imageUrl) {
    return {
      kind: 'empty',
    } as const satisfies ImageField;
  }

  return {
    kind: 'unchanged',
    url: imageUrl,
  } as const satisfies ValidImageField;
};

export const imageUploadField = {
  init: initImageUploadField,
  toPreviewUrl: getImagePreviewUrl,
  toWriteRef: getImageWriteRef,
  schema: ImageFieldSchema,
  validSchema: ValidImageFieldSchema,
};

export const wrapImageUploadProps = ({
  value,
  setValue,
}: {
  value: ImageField;
  setValue: (value: ImageField) => void;
}) => {
  const upload = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setValue({ kind: 'new', previewUrl, file });
  };

  const remove = () => {
    setValue({ kind: 'removed' });
  };

  const preview =
    value.kind === 'new'
      ? value.previewUrl
      : value.kind === 'unchanged'
        ? value.url
        : null;

  const file = value.kind === 'new' ? value.file : null;

  return {
    kind: value.kind,
    upload,
    remove,
    preview,
    file,
  };
};
